/* Pick a photo the client already has.
 *
 * The point of this whole feature is that a photo is stored once. A case study
 * showing a job the crew already photographed should reach for that photo, not
 * ask someone to find the original and upload it a second time.
 *
 * Thumbnails go through AssetImg, so they still render in local development
 * where the bytes are in Miniflare's R2 but CDN_BASE_URL points at the real CDN.
 * Without that every tile here would be a broken image on a developer's machine.
 */
import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { getGalleryAssets, type GalleryAssetSummary } from '@/data/client';
import { Modal } from './Modal';
import { AssetImg } from './AssetImg';

export function GalleryPhotoPicker({ onPick, onClose, taken = [] }: {
  onPick: (assetId: string) => void;
  onClose: () => void;
  /** Already on this case study — shown, but not pickable twice. */
  taken?: string[];
}) {
  const [busy, setBusy] = useState('');
  const already = new Set(taken);

  const { data, fetchNextPage, hasNextPage, isFetching } = useInfiniteQuery({
    queryKey: ['galleryAssets'],
    queryFn: ({ pageParam }) => getGalleryAssets(pageParam as number),
    initialPageParam: 0,
    getNextPageParam: (last) => last.nextOffset ?? undefined,
    staleTime: 30_000,
  });

  const assets: GalleryAssetSummary[] = data?.pages.flatMap((p) => p.assets) ?? [];

  const pick = (a: GalleryAssetSummary) => {
    if (already.has(a.id) || busy) return;
    setBusy(a.id);
    onPick(a.id);
  };

  return (
    <Modal title="Add from the gallery" onClose={onClose}>
      <div className="es-modal__body">
        {isFetching && !assets.length && <p className="es-hint">Loading photos…</p>}

        {!isFetching && !assets.length && (
          <p className="es-hint">
            No photos in the gallery yet. Upload one here and you can file it in an
            album at the same time.
          </p>
        )}

        <div className="es-pickgrid">
          {assets.map((a) => (
            <button type="button" key={a.id}
              className={`es-picktile${already.has(a.id) ? ' is-taken' : ''}`}
              disabled={already.has(a.id) || !!busy}
              title={a.albums.length ? `In ${a.albums.join(', ')}` : 'Not in an album'}
              onClick={() => pick(a)}>
              <AssetImg url={a.url} assetId={a.id} alt="" className="es-picktile__img" />
              {already.has(a.id) && <span className="es-picktile__tag">Added</span>}
              {/* Where a photo already lives is the useful thing to know when
                  choosing between two shots of the same job. */}
              {!already.has(a.id) && !!a.albums.length && (
                <span className="es-picktile__albums">{a.albums.join(' · ')}</span>
              )}
            </button>
          ))}
        </div>

        {hasNextPage && (
          <Button variant="ghost" size="sm" disabled={isFetching} onClick={() => void fetchNextPage()}>
            {isFetching ? 'Loading…' : 'Show more'}
          </Button>
        )}
      </div>
      <div className="es-modal__foot">
        <Button variant="ghost" size="sm" onClick={onClose}>
          <Icon name="x" size={14} /> Done
        </Button>
      </div>
    </Modal>
  );
}
